import React from "react";
import { Grid2, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { BookingScheduleInterface } from "../../features/Booking-Form/typings/booking.types";
import { ScheduleEventContentIcon } from "./schedule-event-content-icon.component";
import { resolveScheduleVisitorCalendarLabel } from "./functions/schedule-visitor-display-name.function";
import { Colleague } from "../../features/Connections/types/Colleague.type";
import { BookingType } from "../../features/Booking-Form/typings/booking-inputs";
import { resolveScheduleEventPlaceName } from "./functions/resolve-schedule-event-place-name.function";

const BOOKING_TYPE_ICON_SIZE_PX = 30;
const BOOKING_TYPE_ICON_GAP = "0.6rem";

type P = {
  schedule: BookingScheduleInterface;
  userId: string;
  bookingUser?: Colleague;
  /** When false (resource calendar), hide conference visitor mail link. */
  showConferenceVisitorContactLink?: boolean;
  /** Optional content below the icon + name row (e.g. resource catering qty). */
  belowNameContent?: React.ReactNode;
};

export const ScheduleEventBookingTypeContents: React.FC<P> = ({
  schedule,
  userId,
  bookingUser,
  showConferenceVisitorContactLink = true,
  belowNameContent
}) => {
  const { t } = useTranslation();

  const bookingName = resolveScheduleEventPlaceName(schedule, t);

  const visitorCalendarLabel = resolveScheduleVisitorCalendarLabel(schedule);
  const showVisitorName = visitorCalendarLabel !== null;

  const nameLabel = showVisitorName ? visitorCalendarLabel : bookingName;
  const nameTestId = showVisitorName
    ? "schedule-popover-booking-visitor-name"
    : "schedule-popover-booking-inven-name";

  const iconCell = (
    <Grid2
      sx={{
        marginRight: BOOKING_TYPE_ICON_GAP,
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        lineHeight: 0
      }}
      data-testid="schedule-popover-bookingType-content"
    >
      <ScheduleEventContentIcon
        schedule={schedule}
        userId={userId}
        iconSize={BOOKING_TYPE_ICON_SIZE_PX}
        bookingUser={bookingUser}
        showConferenceVisitorContactLink={showConferenceVisitorContactLink}
      />
    </Grid2>
  );

  const nameCell = (
    <Grid2 data-testid={nameTestId} sx={{ flexGrow: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: "bold" }}>{nameLabel}</Typography>
    </Grid2>
  );

  if (belowNameContent) {
    return (
      <Grid2
        container
        direction="column"
        data-testid="schedule-popover-bookingType-content-parent"
        sx={{ mb: 1 }}
      >
        <Grid2 container alignItems="center" flexWrap="nowrap">
          {iconCell}
          {nameCell}
        </Grid2>
        <Grid2 sx={{ pl: 0, mt: 0.5, width: "100%" }}>{belowNameContent}</Grid2>
      </Grid2>
    );
  }

  let iconMarginTop: number | string = "0.4rem";
  if (schedule.bookingType === BookingType.CONFERENCEZONE) {
    iconMarginTop = "0.6rem";
  }

  return (
    <Grid2
      container
      alignItems="center"
      data-testid="schedule-popover-bookingType-content-parent"
      flexWrap="nowrap"
      sx={{ mb: 1 }}
    >
      <Grid2
        sx={{
          marginRight: BOOKING_TYPE_ICON_GAP,
          marginTop: iconMarginTop
        }}
        data-testid="schedule-popover-bookingType-content"
      >
        <ScheduleEventContentIcon
          schedule={schedule}
          userId={userId}
          iconSize={BOOKING_TYPE_ICON_SIZE_PX}
          bookingUser={bookingUser}
          showConferenceVisitorContactLink={showConferenceVisitorContactLink}
        />
      </Grid2>
      {nameCell}
    </Grid2>
  );
};
